// components/charts/ProjectComparisonChart.jsx
import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';

// A map to store Chart instances, preventing memory leaks
const chartInstances = {};

// Helper function to determine bar color based on defect rate
const getDefectRateColor = (rate) => {
    if (rate > 10) return 'rgba(220, 53, 69, 0.7)'; // Red for high defect rate
    if (rate > 5) return 'rgba(255, 193, 7, 0.7)';  // Yellow for medium defect rate
    return 'rgba(40, 167, 69, 0.7)';   // Green for low defect rate
};

const ProjectComparisonChart = ({ projects, onProjectClick }) => {
    const chartRef = useRef(null);
    const chartId = 'projectComparisonChart'; // Static ID for this specific chart

    useEffect(() => {
        if (!chartRef.current) {
            console.log('[ProjectComparisonChart] Chart ref not available.');
            return;
        }

        const ctx = chartRef.current.getContext('2d');

        // Destroy the previous chart instance if it exists
        if (chartInstances[chartId]) {
            console.log('[ProjectComparisonChart] Destroying previous chart instance.');
            chartInstances[chartId].destroy();
        }

        if (!projects || projects.length === 0) {
            console.log('[ProjectComparisonChart] No projects data, clearing canvas.');
            // Clear the canvas if there's no data
            ctx.clearRect(0, 0, chartRef.current.width, chartRef.current.height);
            return;
        }

        console.log('[ProjectComparisonChart] Rendering chart with projects:', projects);

        const chartData = {
            labels: projects.map(p => p.project_name),
            datasets: [
                {
                    label: 'Defect Rate (%)',
                    data: projects.map(p => {
                        const stats = p.statistics || {};
                        return stats.total > 0 ? (stats.defective / stats.total) * 100 : 0;
                    }),
                    backgroundColor: projects.map(p => {
                        const stats = p.statistics || {};
                        const rate = stats.total > 0 ? (stats.defective / stats.total) * 100 : 0;
                        return getDefectRateColor(rate);
                    }),
                    borderColor: projects.map(p => {
                        const stats = p.statistics || {};
                        const rate = stats.total > 0 ? (stats.defective / stats.total) * 100 : 0;
                        return getDefectRateColor(rate).replace('0.7', '1');
                    }),
                    borderWidth: 1,
                    yAxisID: 'y',
                    order: 2, // Render behind the total inspections
                },
                {
                    label: 'Total Inspections',
                    data: projects.map(p => p.statistics?.total || 0),
                    backgroundColor: 'rgba(0, 123, 255, 0.2)',
                    borderColor: 'rgba(0, 123, 255, 1)',
                    borderWidth: 1,
                    yAxisID: 'y1',
                    order: 1,
                }
            ]
        };

        const config = {
            type: 'bar',
            data: chartData,
            options: {
                indexAxis: 'y', // This makes the bar chart horizontal
                responsive: true,
                maintainAspectRatio: false,
                onClick: (event, elements) => {
                    if (elements.length > 0 && onProjectClick) {
                        const clickedIndex = elements[0].index;
                        const project = projects[clickedIndex];
                        onProjectClick(project);
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Value'
                        },
                        grid: {
                            color: 'rgba(0,0,0,0.05)'
                        }
                    },
                    y: {
                        position: 'left',
                        title: {
                            display: true,
                            text: 'Parts'
                        },
                        ticks: {
                            autoSkip: false,
                        }
                    },
                    y1: {
                        display: false, // We'll overlay this on the same axis for clarity
                    }
                },
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    tooltip: {
                        callbacks: {
                            title: (context) => context[0].label,
                            label: (context) => {
                                const datasetLabel = context.dataset.label || '';
                                const value = context.parsed.x;
                                if (datasetLabel.includes('Defect Rate')) {
                                    return `${datasetLabel}: ${value.toFixed(2)}%`;
                                }
                                return `${datasetLabel}: ${value}`;
                            },
                            afterBody: (context) => {
                                const dataIndex = context[0].dataIndex;
                                const project = projects[dataIndex];
                                const stats = project.statistics || {};
                                const lastInspectionDate = project.last_inspection ? new Date(project.last_inspection).toLocaleDateString() : 'Never';

                                return [
                                    ``,
                                    `Inspections: ${stats.total || 0}`,
                                    `Model: ${project.model_trained ? 'Trained' : 'No Model'}`,
                                    `Last Activity: ${lastInspectionDate}`
                                ];
                            }
                        }
                    }
                }
            }
        };

        chartInstances[chartId] = new Chart(ctx, config);

        // Cleanup function
        return () => {
            if (chartInstances[chartId]) {
                console.log('[ProjectComparisonChart] Component unmounting, destroying chart.');
                chartInstances[chartId].destroy();
                delete chartInstances[chartId];
            }
        };
    }, [projects, onProjectClick]);

    if (!projects || projects.length === 0) {
        return (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                <div className="text-center">
                    <i className="fas fa-chart-bar fa-3x mb-3"></i>
                    <h6>No Part Data</h6>
                    <p className="small">No projects match the current filters.</p>
                </div>
            </div>
        );
    }

    return <canvas ref={chartRef} style={{ height: '100%', width: '100%' }}></canvas>;
};

export default ProjectComparisonChart;
